import React, { useState, useEffect } from 'react';

const WeatherWidget = ({ isDarkMode }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Underberg coordinates: -29.5833, 29.2833
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?lat=-29.5833&lon=29.2833&appid=YOUR_API_KEY&units=metric&cnt=7`
        );
        
        if (!response.ok) {
          throw new Error('Weather data unavailable');
        }
        
        const data = await response.json();
        setWeatherData(data);
      } catch (err) {
        setError(err.message);
        // Fallback to static data if API fails
        setWeatherData({
          list: [
            { dt: Date.now(), main: { temp: 18, temp_min: 8, temp_max: 22, humidity: 65 }, weather: [{ main: 'Clouds', description: 'Partly cloudy', icon: '02d' }], wind: { speed: 15 } },
            { dt: Date.now() + 86400000, main: { temp: 20, temp_min: 10, temp_max: 25, humidity: 60 }, weather: [{ main: 'Clear', description: 'Sunny', icon: '01d' }], wind: { speed: 12 } },
            { dt: Date.now() + 172800000, main: { temp: 17, temp_min: 7, temp_max: 20, humidity: 70 }, weather: [{ main: 'Rain', description: 'Light rain', icon: '10d' }], wind: { speed: 18 } },
            { dt: Date.now() + 259200000, main: { temp: 19, temp_min: 9, temp_max: 23, humidity: 65 }, weather: [{ main: 'Clouds', description: 'Partly cloudy', icon: '03d' }], wind: { speed: 14 } },
            { dt: Date.now() + 345600000, main: { temp: 21, temp_min: 11, temp_max: 26, humidity: 55 }, weather: [{ main: 'Clear', description: 'Sunny', icon: '01d' }], wind: { speed: 10 } },
            { dt: Date.now() + 432000000, main: { temp: 18, temp_min: 8, temp_max: 22, humidity: 68 }, weather: [{ main: 'Clouds', description: 'Partly cloudy', icon: '02d' }], wind: { speed: 16 } },
            { dt: Date.now() + 518400000, main: { temp: 20, temp_min: 10, temp_max: 24, humidity: 62 }, weather: [{ main: 'Clear', description: 'Sunny', icon: '01d' }], wind: { speed: 13 } }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  const getWeatherIcon = (iconCode) => {
    // OpenWeatherMap 2D flat icons
    return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className={`rounded-lg shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-center">
          <div className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading weather...</div>
        </div>
      </div>
    );
  }

  if (error && !weatherData) {
    return (
      <div className={`rounded-lg shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-center">
          <div className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Weather unavailable</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="mb-4">
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>7-Day Weather Forecast</h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Underberg, South Africa</p>
      </div>
      
      <div className="grid grid-cols-7 gap-2">
        {weatherData?.list?.slice(0, 7).map((day, index) => (
          <div key={index} className={`text-center p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {formatDate(day.dt)}
            </div>
            <div className="flex justify-center my-2">
              <img 
                src={getWeatherIcon(day.weather[0]?.icon)}
                alt={day.weather[0]?.description}
                className="w-12 h-12"
              />
            </div>
            <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {Math.round(day.main.temp)}°C
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {Math.round(day.main.temp_min)}° / {Math.round(day.main.temp_max)}°
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {day.main.humidity}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeatherWidget; 